'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { Check, Crop, Frame, Image as ImageIcon, Palette, ShoppingCart, SlidersHorizontal } from 'lucide-react';
import { ARTWORK_PRINT_PRODUCTS, artworkImageUrl, artworkOrderUrl, isSizeLikelyPrintable, type ArtworkPrintProduct } from '@/lib/artworks';

type ArtworkForChooser = {
  slug: string;
  title: string;
  artist?: string | null;
  imageUrl?: string | null;
  iiifImageId?: string | null;
  iiifImageUrl?: string | null;
  widthPx?: number | null;
  heightPx?: number | null;
  isCommercialUseAllowed: boolean;
};

type RealProductSize = { key: string; label: string; widthMm: number; heightMm: number };
type RealProductOption = { id: string; label: string; type: string; values: Array<{ id: string; label: string; value: string }> };
type RealArtworkProduct = ArtworkPrintProduct & {
  productName?: string;
  localizedName?: string;
  localizedDescription?: string | null;
  mockupImageUrl?: string | null;
  mockupAlt?: string | null;
  sizes: RealProductSize[];
  optionGroups: RealProductOption[];
};

const copyByLocale = {
  nl: {
    chooseProduct: 'Kies je product',
    intro: 'Bekijk direct hoe het kunstwerk uitkomt op echte productfoto’s, formaten en opties.',
    realSizes: 'Werkelijke formaten',
    crop: 'Uitsnede',
    contain: 'Volledig kunstwerk',
    cover: 'Vullend bijsnijden',
    realOptions: 'Werkelijke productopties',
    freeInput: 'Vrij invoerbaar in de configurator.',
    order: 'Kies formaat in configurator',
    rightsBlocked: 'Dit kunstwerk is niet bestelbaar omdat de commerciele gebruiksrechten niet vrij zijn bevestigd.',
    mockupTitle: 'Productweergave',
    mockupText: 'Links staat de echte productfoto uit de catalogus. De drukvlak-preview toont formaatverhouding en uitsnede; prijs en technische controle lopen via de configurator.',
    rightsTitle: 'Bron en rechten',
    rightsText: 'Alleen kunstwerken met CC0/Public Domain-status worden gepubliceerd en bestelbaar gemaakt.',
    actualPhoto: 'Echte productfoto',
    printPreview: 'Drukvlak-preview',
    cropped: 'Vullend bijgesneden',
    full: 'Volledig zichtbaar',
  },
  en: {
    chooseProduct: 'Choose your product',
    intro: 'Preview the artwork with real product photos, actual sizes and available options.',
    realSizes: 'Actual sizes',
    crop: 'Crop',
    contain: 'Full artwork',
    cover: 'Fill and crop',
    realOptions: 'Actual product options',
    freeInput: 'Entered freely in the configurator.',
    order: 'Choose size in configurator',
    rightsBlocked: 'This artwork cannot be ordered because commercial usage rights are not confirmed as free.',
    mockupTitle: 'Product view',
    mockupText: 'The catalog product photo is shown on the left. The print-area preview shows aspect ratio and crop; pricing and technical checks run in the configurator.',
    rightsTitle: 'Source and rights',
    rightsText: 'Only artworks with CC0/Public Domain status are published and orderable.',
    actualPhoto: 'Real product photo',
    printPreview: 'Print-area preview',
    cropped: 'Filled and cropped',
    full: 'Fully visible',
  },
  de: {
    chooseProduct: 'Produkt wählen',
    intro: 'Prüfe das Kunstwerk mit echten Produktfotos, realen Formaten und verfügbaren Optionen.',
    realSizes: 'Tatsächliche Formate',
    crop: 'Ausschnitt',
    contain: 'Ganzes Kunstwerk',
    cover: 'Füllend zuschneiden',
    realOptions: 'Tatsächliche Produktoptionen',
    freeInput: 'Frei im Konfigurator einzugeben.',
    order: 'Format im Konfigurator wählen',
    rightsBlocked: 'Dieses Kunstwerk ist nicht bestellbar, weil die kommerziellen Nutzungsrechte nicht frei bestätigt sind.',
    mockupTitle: 'Produktansicht',
    mockupText: 'Links steht das echte Produktfoto aus dem Katalog. Die Druckflächen-Vorschau zeigt Formatverhältnis und Ausschnitt; Preis und technische Prüfung laufen im Konfigurator.',
    rightsTitle: 'Quelle und Rechte',
    rightsText: 'Nur Kunstwerke mit CC0/Public-Domain-Status werden veröffentlicht und bestellbar gemacht.',
    actualPhoto: 'Echtes Produktfoto',
    printPreview: 'Druckflächen-Vorschau',
    cropped: 'Füllend zugeschnitten',
    full: 'Ganz sichtbar',
  },
  fr: {
    chooseProduct: 'Choisissez votre produit',
    intro: 'Prévisualisez l’œuvre avec de vraies photos produit, les formats réels et les options disponibles.',
    realSizes: 'Formats réels',
    crop: 'Recadrage',
    contain: 'Œuvre complète',
    cover: 'Remplir et recadrer',
    realOptions: 'Options produit réelles',
    freeInput: 'Saisie libre dans le configurateur.',
    order: 'Choisir le format dans le configurateur',
    rightsBlocked: 'Cette œuvre ne peut pas être commandée car les droits commerciaux libres ne sont pas confirmés.',
    mockupTitle: 'Vue produit',
    mockupText: 'La vraie photo produit du catalogue est affichée à gauche. L’aperçu de la zone imprimée montre le ratio et le recadrage; le prix et le contrôle technique passent par le configurateur.',
    rightsTitle: 'Source et droits',
    rightsText: 'Seules les œuvres CC0/Public Domain sont publiées et commandables.',
    actualPhoto: 'Vraie photo produit',
    printPreview: 'Aperçu zone imprimée',
    cropped: 'Rempli et recadré',
    full: 'Entièrement visible',
  },
};

function getCopy(locale: string) {
  return copyByLocale[locale as keyof typeof copyByLocale] || copyByLocale.nl;
}

function localizedProductLabel(product: RealArtworkProduct) {
  return product.localizedName || product.label;
}

function roomLabel(product: RealArtworkProduct, size: RealProductSize, copy: ReturnType<typeof getCopy>) {
  return `${size.label} · ${localizedProductLabel(product)} · ${copy.actualPhoto}`;
}

export function ArtworkProductChooser({
  artwork,
  locale,
  initialProductSlug,
  products,
}: {
  artwork: ArtworkForChooser;
  locale: string;
  initialProductSlug?: string | null;
  products?: RealArtworkProduct[];
}) {
  const copy = getCopy(locale);
  const productList: RealArtworkProduct[] = products?.length
    ? products
    : ARTWORK_PRINT_PRODUCTS.map((item) => ({ ...item, optionGroups: [] }));
  const initialProduct = productList.find((item) => item.slug === initialProductSlug) || productList[0];
  const [productSlug, setProductSlug] = useState(initialProduct.slug);
  const product = productList.find((item) => item.slug === productSlug) || productList[0];
  const [sizeKey, setSizeKey] = useState(initialProduct.sizes[0].key);
  const [fitMode, setFitMode] = useState<'contain' | 'cover'>('cover');
  const [canvasEdge, setCanvasEdge] = useState<'wrap' | 'mirror' | 'white' | 'black'>('wrap');

  const currentSize = product.sizes.find((size) => size.key === sizeKey) || product.sizes[0];
  const printable = isSizeLikelyPrintable(artwork, currentSize);
  const imageUrl = artworkImageUrl(artwork, 1400) || `/api/artworks/${artwork.slug}/image?w=1400`;
  const optionGroups: RealProductOption[] = product.optionGroups;

  const aspectStyle = useMemo(() => ({
    aspectRatio: `${currentSize.widthMm} / ${currentSize.heightMm}`,
  }), [currentSize.heightMm, currentSize.widthMm]);

  const orderUrl = artworkOrderUrl(locale, product.slug, artwork, currentSize.key, fitMode);
  const isCanvasLike = product.slug === 'canvas' || product.slug === 'ingelijst-canvas';
  const canvasEdgeLabel = {
    wrap: locale === 'en' ? 'Image wrap' : locale === 'de' ? 'Motiv umgeschlagen' : locale === 'fr' ? 'Image sur les bords' : 'Afbeelding doorlopend',
    mirror: locale === 'en' ? 'Mirror edge' : locale === 'de' ? 'Spiegelkante' : locale === 'fr' ? 'Bords miroir' : 'Spiegelrand',
    white: locale === 'en' ? 'White edge' : locale === 'de' ? 'Weiße Kante' : locale === 'fr' ? 'Bords blancs' : 'Witte rand',
    black: locale === 'en' ? 'Black edge' : locale === 'de' ? 'Schwarze Kante' : locale === 'fr' ? 'Bords noirs' : 'Zwarte rand',
  };

  return (
    <section className="grid gap-8 lg:grid-cols-[minmax(0,0.92fr)_minmax(420px,0.78fr)]">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="grid gap-0 md:grid-cols-[1.05fr_0.95fr]">
          <div className="relative min-h-[420px] bg-slate-100">
            {product.mockupImageUrl ? (
              <img
                src={product.mockupImageUrl}
                alt={product.mockupAlt || localizedProductLabel(product)}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full items-center justify-center bg-slate-100 text-sm font-bold text-slate-500">
                {localizedProductLabel(product)}
              </div>
            )}
            <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-black uppercase tracking-wide text-slate-700 shadow-sm">
              {copy.actualPhoto}
            </div>
          </div>

          <div className="flex min-h-[420px] flex-col bg-slate-50 p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-black uppercase tracking-wide text-slate-500">{copy.printPreview}</div>
                <div className="mt-1 text-sm font-bold text-slate-700">{roomLabel(product, currentSize, copy)}</div>
              </div>
              <Frame className="h-5 w-5 text-slate-500" />
            </div>
            <div className="flex flex-1 items-center justify-center rounded-xl bg-white p-8 shadow-inner">
              <div className={`relative max-h-[330px] w-full max-w-[390px] overflow-visible bg-white shadow-2xl ${product.slug === 'behangcirkel' ? 'rounded-full' : product.slug === 'fototegel' ? 'rounded-md ring-8 ring-white' : ''}`} style={aspectStyle}>
                {isCanvasLike && (
                  <>
                    <div
                      className={`absolute -right-4 top-3 h-full w-4 skew-y-[-10deg] shadow-lg ${canvasEdge === 'white' ? 'bg-white' : canvasEdge === 'black' ? 'bg-slate-950' : 'overflow-hidden bg-slate-700'}`}
                    >
                      {(canvasEdge === 'wrap' || canvasEdge === 'mirror') && (
                        <img src={imageUrl} alt="" className={`h-full w-full object-cover opacity-80 ${canvasEdge === 'mirror' ? '-scale-x-100' : ''}`} />
                      )}
                    </div>
                    <div
                      className={`absolute -bottom-4 left-3 h-4 w-full skew-x-[-10deg] ${canvasEdge === 'white' ? 'bg-white' : canvasEdge === 'black' ? 'bg-slate-950' : 'overflow-hidden bg-slate-800'}`}
                    >
                      {(canvasEdge === 'wrap' || canvasEdge === 'mirror') && (
                        <img src={imageUrl} alt="" className={`h-full w-full object-cover opacity-70 ${canvasEdge === 'mirror' ? '-scale-y-100' : ''}`} />
                      )}
                    </div>
                  </>
                )}
                <img
                  src={imageUrl}
                  alt={`${artwork.title}${artwork.artist ? ` van ${artwork.artist}` : ''} als ${localizedProductLabel(product)}`}
                  className={`relative h-full w-full ${fitMode === 'cover' ? 'object-cover' : 'object-contain'} ${product.slug === 'behangcirkel' ? 'rounded-full' : ''}`}
                />
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-white p-3 text-sm font-semibold text-slate-700 shadow-sm">
              <div className="flex flex-wrap items-center justify-center gap-3">
                <span className="inline-flex items-center gap-1.5"><Frame className="h-4 w-4" />{localizedProductLabel(product)}</span>
                <span>{currentSize.label}</span>
                <span className="inline-flex items-center gap-1.5"><Crop className="h-4 w-4" />{fitMode === 'contain' ? copy.full : copy.cropped}</span>
                {isCanvasLike && <span>{canvasEdgeLabel[canvasEdge]}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <h2 className="text-2xl font-black text-slate-950">{copy.chooseProduct}</h2>
          <p className="mt-2 text-slate-600">{copy.intro}</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {productList.map((item) => (
            <button
              key={item.slug}
              type="button"
              onClick={() => {
                setProductSlug(item.slug);
                setSizeKey(item.sizes[0].key);
              }}
              className={`rounded-xl border p-4 text-left transition ${item.slug === product.slug ? 'border-rose-500 bg-rose-50 ring-2 ring-rose-100' : 'border-slate-200 bg-white hover:border-rose-200'}`}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="font-black text-slate-950">{localizedProductLabel(item)}</span>
                {item.slug === product.slug && <Check className="h-4 w-4 text-rose-700" />}
              </span>
              <span className="mt-1 block line-clamp-2 text-sm leading-5 text-slate-600">{item.localizedDescription || item.description}</span>
            </button>
          ))}
        </div>

        <div>
          <h3 className="text-sm font-black uppercase tracking-wide text-slate-500">{copy.realSizes} ({product.sizes.length})</h3>
          <div className="mt-2 max-h-36 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2">
            <div className="flex flex-wrap gap-2">
            {product.sizes.map((size) => {
              const sizeOk = isSizeLikelyPrintable(artwork, size);
              return (
                <button
                  key={size.key}
                  type="button"
                  onClick={() => setSizeKey(size.key)}
                  className={`rounded-full border px-4 py-2 text-sm font-bold transition ${size.key === currentSize.key ? 'border-slate-950 bg-slate-950 text-white' : sizeOk ? 'border-slate-200 bg-white text-slate-700 hover:border-slate-400' : 'border-amber-200 bg-amber-50 text-amber-800'}`}
                >
                  {size.label}
                </button>
              );
            })}
            </div>
          </div>
          {!printable && (
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900">
              {locale === 'fr' ? 'Ce format peut être limite pour une impression nette. Choisissez un format plus petit ou vérifiez manuellement.' : locale === 'de' ? 'Dieses Format kann für eine scharfe Druckqualität knapp sein. Wählen Sie besser ein kleineres Format oder prüfen Sie manuell.' : locale === 'en' ? 'This size may be tight for sharp print quality. Choose a smaller size or check manually.' : 'Dit formaat kan krap zijn voor scherpe printkwaliteit. Kies liever een kleiner formaat of controleer handmatig.'}
            </p>
          )}
        </div>

        {isCanvasLike && (
          <div>
            <h3 className="text-sm font-black uppercase tracking-wide text-slate-500">
              {locale === 'en' ? 'Canvas edge' : locale === 'de' ? 'Leinwandkante' : locale === 'fr' ? 'Bords de la toile' : 'Canvas zijkanten'}
            </h3>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {(['wrap', 'mirror', 'white', 'black'] as const).map((edge) => (
                <button
                  key={edge}
                  type="button"
                  onClick={() => setCanvasEdge(edge)}
                  className={`rounded-xl border px-3 py-2 text-sm font-black ${canvasEdge === edge ? 'border-rose-500 bg-rose-50 text-rose-800' : 'border-slate-200 bg-white text-slate-700'}`}
                >
                  {canvasEdgeLabel[edge]}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-sm font-black uppercase tracking-wide text-slate-500">{copy.crop}</h3>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFitMode('contain')}
              className={`rounded-xl border px-4 py-3 text-sm font-black ${fitMode === 'contain' ? 'border-rose-500 bg-rose-50 text-rose-800' : 'border-slate-200 bg-white text-slate-700'}`}
            >
              {copy.contain}
            </button>
            <button
              type="button"
              onClick={() => setFitMode('cover')}
              className={`rounded-xl border px-4 py-3 text-sm font-black ${fitMode === 'cover' ? 'border-rose-500 bg-rose-50 text-rose-800' : 'border-slate-200 bg-white text-slate-700'}`}
            >
              {copy.cover}
            </button>
          </div>
        </div>

        {optionGroups.length > 0 && (
          <div>
            <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-slate-500">
              <SlidersHorizontal className="h-4 w-4" /> {copy.realOptions}
            </h3>
            <div className="mt-2 space-y-3">
              {optionGroups.map((option) => (
                <div key={option.id} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-black text-slate-950">{option.label}</div>
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-500">{option.values.length || option.type}</span>
                  </div>
                  {option.values.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {option.values.slice(0, 8).map((value) => (
                        <span key={`${option.id}-${value.id}`} className="rounded-full bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
                          {value.label}
                        </span>
                      ))}
                      {option.values.length > 8 && (
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-600">+{option.values.length - 8}</span>
                      )}
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-slate-600">{copy.freeInput}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {artwork.isCommercialUseAllowed ? (
          <Link
            href={orderUrl}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-rose-600 px-6 py-4 text-base font-black text-white transition hover:bg-rose-700"
          >
            <ShoppingCart className="h-5 w-5" />
            {copy.order}
          </Link>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-900">
            {copy.rightsBlocked}
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          <div className="mb-1 flex items-center gap-2 font-black text-slate-950"><Palette className="h-4 w-4" />{copy.mockupTitle}</div>
          {copy.mockupText}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          <div className="mb-1 flex items-center gap-2 font-black text-slate-950"><ImageIcon className="h-4 w-4" />{copy.rightsTitle}</div>
          {copy.rightsText}
        </div>
      </div>
    </section>
  );
}
